import { Component, computed, EventEmitter, input, Output, Signal } from '@angular/core';
import { DrilldownResult } from '@r1-compass-library/models/reporting/aggregate-response.model';
import { Color, NgxChartsModule } from '@swimlane/ngx-charts';
import { RowSelectedEvent } from '../dialogs/action-activity-dialog/action-activity-dialog.component';
import { ActivityChartData } from '../dialogs/action-activity-dialog/action-activity-dialog.service';

@Component({
    selector: 'r1-compass-app-stacked-drilldown-chart',
    imports: [NgxChartsModule],
    templateUrl: './stacked-drilldown-chart.component.html',
    styleUrl: './stacked-drilldown-chart.component.scss',
})
export class StackedDrilldownChartComponent {
    view = '';
    showXAxis = true;
    showYAxis = true;
    gradient = false;
    showLegend = true;
    showXAxisLabel = true;
    yAxisLabel = 'Play ID';
    showYAxisLabel = true;
    xAxisLabel = 'Count';
    showDataLabel = true;
    showGridLines = true;
    barPadding = 6;
    colorScheme = { domain: ['#93d500', '#1174b6', '#ff9933', '#f3b88c'] } as Color;

    chartData = input.required<ActivityChartData[]>();
    normalizedChartData = computed(() => this.normalizeChartData(this.chartData()));

    /**
     * Replaces the x-axis value with an empty string if it is a decimal.
     */
    removeDecimalXAxisValues(input: any) {
        const num = String(input);
        const split = num.split('.');
        if (split.length <= 1) {
            return split[0];
        } else {
            return '';
        }
    }

    results: Signal<DrilldownResult[]> = computed(() => {
        return this.transformChartData(this.normalizedChartData());
    });

    graphHeight: Signal<number | 'calc(100% - 75px)'> = computed(() => {
        const resultHeight: number = this.results().length * 30;

        return resultHeight < 700 ? 'calc(100% - 75px)' : resultHeight;
    });

    @Output()
    viewDetails: EventEmitter<RowSelectedEvent> = new EventEmitter();

    /**
     * Arrays of aggregate data need to have the same set of keys in each, otherwise you can get mismatches in the color mapping.
     * WRONG: [
     *  [{ key: 1, agg: 12}, { key: 2, agg: 34}],
     *  [{ key: 2, agg: 56 }, { key: 3, agg: 78}]
     * ]
     * RIGHT: [
     *  [{ key: 1, agg: 12}, { key: 2, agg: 34}, { key: 3, agg: 0 }],
     *  [{ key: 1, agg: 0}, { key: 2, agg: 56 }, { key: 3, agg: 78}]
     * ]
     */
    private normalizeChartData(chartData: ActivityChartData[]) {
        const normalizedChartData = [...chartData];
        const allKeys = new Set(normalizedChartData.map(data => data.values).flatMap(res => res.map(res => res.key)));
        normalizedChartData
            .map(data => data.values)
            .forEach(aggregateRes => {
                allKeys.forEach(key => {
                    const currentAggKeys = aggregateRes.map(agg => agg.key);
                    if (!currentAggKeys.includes(key)) {
                        aggregateRes.push({ key: key, aggregate: 0 });
                    }
                });
            });
        return normalizedChartData;
    }

    private transformChartData(chartData: ActivityChartData[]): DrilldownResult[] {
        const uniqKeyArr: string[] = this.getRowsFromData(this.normalizedChartData());
        const results: DrilldownResult[] = [];

        if (uniqKeyArr) {
            uniqKeyArr.forEach(appId => {
                const result: DrilldownResult = {
                    name: appId,
                    series: [],
                };
                chartData.forEach(chartSeries => {
                    const matchedValue = chartSeries.values.find(value => value.key === appId);
                    if (matchedValue) {
                        result.series.push({
                            name: chartSeries.name,
                            value: matchedValue.aggregate,
                        });
                    }
                });
                results.push(result);
            });
        }
        return results;
    }

    private getRowsFromData(chartData: ActivityChartData[]): string[] {
        const uniqKeyArr: string[] = [];
        chartData.forEach(chartSeries => {
            chartSeries.values.forEach(val => {
                if (val.key && !uniqKeyArr.includes(val.key)) {
                    uniqKeyArr.push(val.key);
                }
            });
        });
        return uniqKeyArr;
    }
}
